package com.siriusxm.cmc.utils

public val arnRegex: Regex = Regex("""^arn:aws:[a-z0-9-]+:[a-z]{2}-[a-z]+-\d{1}:\d{12}:[^:]+(:[^:]+)*$""")

/**
 * Validate an arn. Just validates the general format, requiring a valid account number, a valid
 * region, and some other checks, but doesn't validate service names or details.
 */
public fun validateArn(arn: String): Boolean = arnRegex.matches(arn)

// the number of top-level components an arn contains (separated by colons)
private const val ARN_COMPONENT_COUNT = 6

private const val ARN_INDEX_PARTITION = 1
private const val ARN_INDEX_VENDOR = 2
private const val ARN_INDEX_REGION = 3
private const val ARN_INDEX_NAMESPACE = 4
private const val ARN_INDEX_RELATIVE_ID = 5

/**
 * Parse an ARN. This is largely lifted from the Kotlin AWS SDK, but it's not
 * a public API there.
 */
public fun Arn(value: String?): Arn? =
    value?.let {
        val split = it.split(':', limit = ARN_COMPONENT_COUNT)
        if (split[0] != "arn") return null
        if (split.size != ARN_COMPONENT_COUNT) return null
        if (split[ARN_INDEX_PARTITION].isEmpty() || split[ARN_INDEX_VENDOR].isEmpty() || split[ARN_INDEX_RELATIVE_ID].isEmpty()) return null

        // There are some variations of ARNs where the resource has colons, so we'll just take
        // the end of the Arn and join it back up for the resource
        val resource = split.drop(ARN_INDEX_RELATIVE_ID).joinToString(":")

        return Arn(
            split[ARN_INDEX_PARTITION],
            split[ARN_INDEX_VENDOR],
            split[ARN_INDEX_REGION],
            split[ARN_INDEX_NAMESPACE],
            resource
        )
    }

public fun String.arn(): Arn? = Arn(this)

/**
 * Represents a parsed form of an ARN (Amazon Resource Name).
 * @param resource the full resource string
 */
public data class Arn(
    val partition: String,
    val service: String,
    val region: String,
    val accountId: String,
    /**
     * The resource string, which is essentially anything after the account id
     */
    val resource: String
) {
    /**
     * The resource split by colon or slash, this likely isn't that useful
     */
    val resourceId: List<String>
        get() {
            return resource.split(':', '/')
        }

    /**
     * The resource type, the first part of the resource string. This may not
     * always be what you're looking for based on the type.
     */
    val resourceType: String
        get() {
            return resourceId.firstOrNull() ?: ""
        }

    /**
     * The resource name, or the data after the first slash in the resource
     */
    val resourceName: String
        get() {
            return resource.substringAfterLast('/')
        }

    public fun arnString(): String {
        return "arn:${partition}:${service}:${region}:${accountId}:${resource}"
    }
}